/**
 * SocialPulseSubPages — SP-branded landings for /places, /causes and
 * /community. Each is a lightweight editorial hub that matches the new
 * SocialPulse palette (emerald + violet + blue + orange + lavender) with
 * no dark surfaces. Real data lives on the sister-site legacy pages —
 * these landings orient the visitor and jump them into the specialist
 * flows.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Users, HeartHandshake, Trees, Globe2,
  Sparkles, Calendar, MessageCircle, HandHeart, BookOpen,
  Newspaper, Compass, Loader2,
} from "lucide-react";
import { API } from "@/lib/api";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const TINTS = {
  violet:   { bg: "bg-violet-50",   text: "text-violet-700",   ring: "ring-violet-200",   bar: "from-violet-200 to-violet-100" },
  emerald:  { bg: "bg-emerald-50",  text: "text-emerald-700",  ring: "ring-emerald-200",  bar: "from-emerald-200 to-emerald-100" },
  blue:     { bg: "bg-blue-50",     text: "text-blue-700",     ring: "ring-blue-200",     bar: "from-blue-200 to-blue-100" },
  orange:   { bg: "bg-orange-50",   text: "text-orange-700",   ring: "ring-orange-200",   bar: "from-orange-200 to-orange-100" },
  rose:     { bg: "bg-rose-50",     text: "text-rose-700",     ring: "ring-rose-200",     bar: "from-rose-200 to-rose-100" },
  teal:     { bg: "bg-teal-50",     text: "text-teal-700",     ring: "ring-teal-200",     bar: "from-teal-200 to-teal-100" },
};

function Shell({ testid, eyebrow, eyebrowIcon: EyebrowIcon, headlineLead, accent, description, ctas, children }) {
  return (
    <div className="min-h-screen bg-white" data-testid={testid}>
      <SocialPulseHeader />

      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(140deg, #F0F6FF 0%, #F5F1FA 40%, #ECFDF5 100%)",
        }}
      >
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-16 pb-10 sm:pb-14 relative">
          <div
            aria-hidden
            className="absolute -top-16 -left-16 w-72 h-72 rounded-full blur-3xl opacity-45 pointer-events-none"
            style={{ background: "radial-gradient(circle, #A7F3D0 0%, transparent 70%)" }}
          />
          <div
            aria-hidden
            className="absolute top-8 right-0 w-80 h-80 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(circle, #DDD6FE 0%, transparent 75%)" }}
          />

          <div className="relative max-w-3xl">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[.22em] text-violet-700">
              <EyebrowIcon className="w-3.5 h-3.5" /> {eyebrow}
            </div>
            <h1
              className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl lg:text-5xl"
              data-testid={`${testid}-h1`}
            >
              {headlineLead}{" "}
              <span className="text-emerald-700">{accent}</span>
            </h1>
            <p className="mt-4 sm:mt-5 text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl">
              {description}
            </p>
            {ctas && (
              <div className="mt-6 flex flex-wrap gap-3">{ctas}</div>
            )}
          </div>
        </div>
      </section>

      {children}

      <SocialPulseFooter />
    </div>
  );
}

function Section({ title, subtitle, id, children }) {
  return (
    <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-14" id={id}>
      <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-1 text-slate-500 text-sm">{subtitle}</p>
      )}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function TileCard({ testid, title, lead, tint, icon: Icon, href, meta }) {
  const t = TINTS[tint] || TINTS.violet;
  return (
    <li>
      <Link
        to={href}
        data-testid={testid}
        className="group flex flex-col rounded-2xl ring-1 ring-slate-200 bg-white overflow-hidden hover:ring-violet-300 hover:shadow-md transition-all h-full"
      >
        <div className={`h-1.5 bg-gradient-to-r ${t.bar}`} />
        <div className="p-5 flex-1 flex flex-col gap-3">
          <span className={`w-11 h-11 grid place-items-center rounded-2xl ${t.bg} ${t.text} ring-1 ${t.ring}`}>
            <Icon className="w-5 h-5" strokeWidth={2.2} />
          </span>
          <div>
            <h3 className={`font-serif font-bold text-lg leading-tight ${t.text}`}>{title}</h3>
            {meta && (
              <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[.15em] text-slate-500">
                {meta}
              </p>
            )}
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{lead}</p>
          </div>
          <span className="mt-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-500 group-hover:text-violet-700 pt-2">
            Open <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </Link>
    </li>
  );
}

function CtaButton({ to, tone, testid, children }) {
  const cls = tone === "primary"
    ? "inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shadow-sm"
    : "inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-5 py-2.5 text-sm transition-colors bg-white/70 backdrop-blur";
  return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

/* ─────────────────── /places (moved) ─────────────────── */
// /places now lives in its own file: `SocialPulsePlaces.jsx`. This module
// keeps /causes and /community.

/* ─────────────────── /causes ─────────────────── */

const CAUSE_CATS = [
  { title: "Health & Wellbeing", lead: "Support local clinics and health programs.",       tint: "rose",    icon: HeartHandshake, href: "/causes?cat=health" },
  { title: "Education",          lead: "Scholarships, school supplies, tutoring.",         tint: "blue",    icon: BookOpen,       href: "/causes?cat=education" },
  { title: "Environment",        lead: "Trees, cleanup, wildlife and climate.",            tint: "emerald", icon: Trees,          href: "/causes?cat=environment" },
  { title: "Food & Basics",      lead: "Community meals, food banks and pantries.",        tint: "orange",  icon: HandHeart,      href: "/causes?cat=food" },
  { title: "Disaster Relief",    lead: "Floods, cyclones, and rapid response.",            tint: "violet",  icon: Sparkles,       href: "/causes?cat=disaster" },
  { title: "Community",          lead: "Neighborhood projects and local groups.",          tint: "teal",    icon: Users,          href: "/causes?cat=community" },
];

export function SocialPulseCauses() {
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [preview, setPreview] = useState({ loading: false, count: 0 });

  useEffect(() => {
    document.title = "Causes — Help a cause across the SocialPulse network";
    setMeta("description", "Discover causes to volunteer, donate to, or lift up. Every cause is verified and lands on the SocialPulse network.");
    setLink("canonical", "https://socialpulse.com/causes");
    setMeta("robots", "index, follow");
  }, []);

  // On mount, ask the backend where the visitor is (IP geo) so we can
  // pre-fill the "near me" filter without asking for GPS permission.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/geo/ip`, { timeout: 4000 }).then((r) => {
      if (cancelled) return;
      const d = r.data || {};
      if (d.city) setCity(d.city);
      if (d.country_name) setCountry(d.country_name);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Whenever the locality changes, ask backend how many causes match.
  useEffect(() => {
    const params = {};
    if (city) params.city = city;
    if (country) params.country = country;
    setPreview({ loading: true, count: 0 });
    axios.get(`${API}/causes`, { params: { ...params, limit: 1 }, timeout: 4000 })
      .then((r) => setPreview({ loading: false, count: r.data?.total ?? (r.data?.items?.length ?? 0) }))
      .catch(() => setPreview({ loading: false, count: 0 }));
  }, [city, country]);

  function detectLocation() {
    if (!navigator.geolocation) return;
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const r = await axios.get(`${API}/geo/reverse`, {
          params: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          timeout: 5000,
        });
        setCity(r.data?.city || "");
        setCountry(r.data?.country || "");
      } catch { /* ignore */ }
      setDetecting(false);
    }, () => setDetecting(false), { timeout: 8000 });
  }

  const causesHref = (() => {
    const p = new URLSearchParams();
    if (city) p.set("city", city);
    if (country) p.set("country", country);
    return `/causes?${p.toString()}`;
  })();

  return (
    <Shell
      testid="sp-causes-page"
      eyebrow="Causes"
      eyebrowIcon={HeartHandshake}
      description="Local causes verified by the community — from health drives to disaster relief. Volunteer your time, donate what you can, or share a cause with your network."
      headlineLead="Give a hand. Lift a"
      accent="community."
      ctas={<>
        <CtaButton to="/causes?action=start" tone="primary" testid="sp-causes-cta-start">Start a cause <ArrowRight className="w-4 h-4" /></CtaButton>
        <CtaButton to="/find-help" tone="ghost" testid="sp-causes-cta-help">Ask for help</CtaButton>
      </>}
    >
      {/* Locality matcher */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pt-10">
        <div
          className="rounded-3xl ring-1 ring-violet-200 p-5 sm:p-6"
          style={{ background: "linear-gradient(135deg,#ECFDF5 0%, #F5F1FA 55%, #FEF3C7 100%)" }}
          data-testid="sp-causes-match"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[.22em] text-emerald-700">Cause match</p>
              <h2 className="mt-2 font-serif font-bold text-slate-900 text-xl sm:text-2xl leading-tight">
                Show me causes near…
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Pre-filled from your location. Tweak or clear to widen the net.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 md:min-w-[440px]" data-testid="sp-causes-match-inputs">
              <label className="relative flex-1 min-w-0">
                <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  data-testid="sp-causes-city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="w-full h-11 pl-9 pr-3 rounded-full ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none text-sm text-slate-800 placeholder:text-slate-400 bg-white"
                />
              </label>
              <label className="relative flex-1 min-w-0">
                <Globe2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  data-testid="sp-causes-country"
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="Country"
                  className="w-full h-11 pl-9 pr-3 rounded-full ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none text-sm text-slate-800 placeholder:text-slate-400 bg-white"
                />
              </label>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={detectLocation}
              disabled={detecting}
              data-testid="sp-causes-detect"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white ring-1 ring-slate-200 hover:ring-emerald-400 text-emerald-700 text-xs font-semibold transition-colors"
            >
              {detecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
              Use my location
            </button>
            <button
              type="button"
              onClick={() => { setCity(""); setCountry(""); }}
              data-testid="sp-causes-clear"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-slate-600 hover:bg-white/70 text-xs font-semibold transition-colors"
            >
              Clear
            </button>
            <span className="ml-auto text-sm text-slate-700">
              {preview.loading ? (
                <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Matching…</span>
              ) : preview.count > 0 ? (
                <><span className="font-semibold text-emerald-700">{preview.count}</span> cause{preview.count === 1 ? "" : "s"} match your area.</>
              ) : (
                <span className="text-slate-500">No matches yet — widen your search.</span>
              )}
            </span>
            <Link
              to={causesHref}
              data-testid="sp-causes-view-matches"
              className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 py-2 text-sm transition-colors shadow-sm"
            >
              View matches <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Section title="Browse by category">
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CAUSE_CATS.map((c) => (
            <TileCard key={c.title} testid={`sp-causes-cat-${c.title.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`} {...c} />
          ))}
        </ul>
      </Section>
      <Section title="How SocialPulse verifies causes" subtitle="Every cause is checked by moderators before it goes live.">
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { n: "1", title: "You submit",   lead: "Share your cause, photos, and any supporting docs." },
            { n: "2", title: "We verify",    lead: "Moderators check ID, links and community reputation." },
            { n: "3", title: "It goes live", lead: "Approved causes surface on relevant place & topic pages." },
          ].map((s) => (
            <li key={s.n} className="rounded-2xl ring-1 ring-slate-200 bg-white p-5">
              <span className="w-9 h-9 grid place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-violet-600 text-white font-bold text-sm shadow-sm">
                {s.n}
              </span>
              <h3 className="mt-3 font-serif font-bold text-slate-900 text-lg leading-tight">{s.title}</h3>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.lead}</p>
            </li>
          ))}
        </ol>
      </Section>
    </Shell>
  );
}

/* ─────────────────── /community ─────────────────── */

const COMMUNITY_TRACKS = [
  { title: "Neighborhood",      lead: "Local groups, meetups and neighborhood chat.",           tint: "emerald", icon: Users,         href: "/topics/community" },
  { title: "Volunteer",         lead: "Offer or find help around your area.",                    tint: "violet",  icon: HandHeart,     href: "/help?mode=offer" },
  { title: "Events",            lead: "Post and discover community events nearby.",             tint: "orange",  icon: Calendar,      href: "/events" },
  { title: "Discussions",       lead: "Have your say on things that matter locally.",           tint: "blue",    icon: MessageCircle, href: "/topics/discussions" },
  { title: "Alumni & Schools",  lead: "Reconnect with former classmates and communities.",       tint: "rose",    icon: BookOpen,      href: "/topics/education" },
  { title: "Local News",        lead: "Voices from the neighborhood, curated by residents.",     tint: "teal",    icon: Newspaper,     href: "/topics/news" },
];

export function SocialPulseCommunity() {
  useEffect(() => {
    document.title = "Community — SocialPulse — Meet neighbors, share stories, help out";
    setMeta("description", "SocialPulse community — neighborhood groups, events, volunteer opportunities, discussions and alumni across the network.");
    setLink("canonical", "https://socialpulse.com/community");
    setMeta("robots", "index, follow");
  }, []);

  return (
    <Shell
      testid="sp-community-page"
      eyebrow="Community"
      eyebrowIcon={Users}
      description="Every SocialPulse pulse is powered by the people who live there. Join a neighborhood, offer help, share your story or discover an event nearby."
      headlineLead="Meet the people who make a place a"
      accent="home."
      ctas={<>
        <CtaButton to="/blog/write" tone="primary" testid="sp-community-cta-story">Share a story <ArrowRight className="w-4 h-4" /></CtaButton>
        <CtaButton to="/help" tone="ghost" testid="sp-community-cta-help">Find / offer help</CtaButton>
      </>}
    >
      <Section title="Ways to join in">
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {COMMUNITY_TRACKS.map((c) => (
            <TileCard key={c.title} testid={`sp-community-track-${c.title.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`} {...c} />
          ))}
        </ul>
      </Section>
      <Section title="Community principles" subtitle="What we ask from every member of the SocialPulse network.">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["Be constructive",  "Disagreement is welcome — cruelty is not."],
            ["Keep it local",    "Post about the place you know best."],
            ["Cite your sources","Reference facts get more trust when they're linked."],
            ["Protect privacy",  "No doxxing, no unfair public exposure."],
          ].map(([t, l]) => (
            <li key={t} className="rounded-2xl ring-1 ring-violet-200 bg-violet-50/40 p-5">
              <p className="font-serif font-bold text-violet-800 text-lg">{t}</p>
              <p className="mt-1 text-sm text-slate-700 leading-relaxed">{l}</p>
            </li>
          ))}
        </ul>
      </Section>
    </Shell>
  );
}
